import { Box, Title, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import { CurrencySelectorCurrencyFragment } from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import TextArea from 'modules/core/components/TextArea'
import { validateUrl } from 'modules/core/components/TextEditor/utils/url'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import ContactInfo from 'modules/finance/components/Contacts/ContactInfo'
import { FinanceContactsSelector } from 'modules/finance/components/Contacts/FinanceContactsSelector'
import CurrencySelector from 'modules/finance/components/CurrencySelector'
import type { WizardData } from 'modules/procurement/components/purchaseRequests/NewRequestModal/types'

interface Props {
  defaultCurrency?: string
  currencies?: CurrencySelectorCurrencyFragment[]
}

const GeneralInformationStep: React.FC<WizardStepRenderParams<WizardData> & Props> = ({
  defaultCurrency,
  currencies,
  data,
  onDataChanged,
}) => {
  const today = moment().startOf('day')
  const isNotValid = (data.url && !validateUrl(data.url)) || undefined
  const {
    company: { id: companyId },
  } = useCurrentSession()

  return (
    <Box gap="s24">
      <Title
        description={i18n.t(
          'procurement.purchase_requests.new_request_wizard.steps.general_information.description'
        )}
        title={i18n.t(
          'procurement.purchase_requests.new_request_wizard.steps.general_information.title'
        )}
      />
      <Box gap="s12" paddingRight="s24">
        <TextField
          mandatory
          value={data.description}
          label={i18n.t(
            'procurement.purchase_requests.new_request_wizard.steps.general_information.fields.description'
          )}
          onChange={(value) => onDataChanged({ ...data, description: value })}
        />
        <Box flexDirection="row" gap="s24">
          <TextField
            type="number"
            value={data.cost}
            label={i18n.t(
              'procurement.purchase_requests.new_request_wizard.steps.general_information.fields.cost'
            )}
            onChange={(value) => onDataChanged({ ...data, cost: value })}
          />

          <CurrencySelector
            defaultValue={data.currency ?? defaultCurrency}
            currencies={currencies}
            onChange={(value) => onDataChanged({ ...data, currency: value })}
          />

          <DayPicker
            label={i18n.t(
              'procurement.purchase_requests.new_request_wizard.steps.general_information.fields.deadline.label'
            )}
            placeholder={i18n.t(
              'procurement.purchase_requests.new_request_wizard.steps.general_information.fields.deadline.placeholder'
            )}
            value={data?.deadline}
            onChange={(value) => onDataChanged({ ...data, deadline: value })}
            minDate={today}
          />
        </Box>
        <Box gap="s16" paddingRight="s24">
          <FinanceContactsSelector
            value={data.vendor}
            onSelect={(formVendorSelected) => {
              if (formVendorSelected && !!formVendorSelected.id) {
                onDataChanged({
                  ...data,
                  vendor: formVendorSelected,
                })
              }
            }}
            label={i18n.t(
              'procurement.purchase_requests.new_request_wizard.steps.general_information.fields.vendor'
            )}
            type="finance_contact"
            queryParams={{
              companyId,
              contactCreateType: 'vendor',
            }}
            variant={'gamma'}
          />
          {data.vendor && (
            <ContactInfo
              showTitle={false}
              contact={data.vendor}
              showLinkToEdit={false}
              as={'vendor'}
            />
          )}
        </Box>
        <TextField
          value={data.url}
          label={i18n.t(
            'procurement.purchase_requests.new_request_wizard.steps.general_information.fields.url'
          )}
          onChange={(value) => onDataChanged({ ...data, url: value })}
          isNotValid={isNotValid}
          error={
            isNotValid
              ? i18n.t(
                  'procurement.purchase_requests.new_request_wizard.steps.general_information.url_not_valid'
                )
              : undefined
          }
        />

        <TextArea
          value={data.additionalInformation}
          label={i18n.t(
            'procurement.purchase_requests.new_request_wizard.steps.general_information.fields.additional_information'
          )}
          placeholder={i18n.t(
            'procurement.purchase_requests.new_request_wizard.steps.general_information.fields.additional_information_placeholder'
          )}
          onChange={(value) => onDataChanged({ ...data, additionalInformation: value })}
          minRows={2}
        />
      </Box>
    </Box>
  )
}

export default GeneralInformationStep
